// ============================================================
// Update Discipline Modal Component
// ============================================================

import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Stack,
  IconButton,
  Avatar,
  Paper,
  CircularProgress,
} from '@mui/material';
import { X, ShieldAlert, CheckCircle2, AlertTriangle, AlertCircle, ShieldOff } from 'lucide-react';

export const STATUS_CONFIG = {
  SATISFACTORY: {
    color: 'success',
    bgColor: '#DCFCE7',
    borderColor: '#22C55E',
    textColor: '#15803D',
    iconColor: '#16A34A',
    icon: CheckCircle2,
    label: 'Satisfactory',
  },
  WARNING: {
    color: 'warning',
    bgColor: '#FEF9C3',
    borderColor: '#EAB308',
    textColor: '#854D0E',
    iconColor: '#CA8A04',
    icon: AlertTriangle,
    label: 'Warning',
  },
  MAJOR_WARNING: {
    color: 'warning',
    bgColor: '#FFEDD5',
    borderColor: '#F97316',
    textColor: '#9A3412',
    iconColor: '#EA580C',
    icon: AlertCircle,
    label: 'Major Warning',
  },
  CRITICAL: {
    color: 'error',
    bgColor: '#FEE2E2',
    borderColor: '#EF4444',
    textColor: '#991B1B',
    iconColor: '#DC2626',
    icon: ShieldOff,
    label: 'Critical',
  },
};

export function UpdateDisciplineModal({ open, onClose, staff, statusEnums = [], onSubmit, loading }) {
  const [status, setStatus] = useState('SATISFACTORY');
  const [remarks, setRemarks] = useState('');

  useEffect(() => {
    if (staff) {
      const initialStatus =
        typeof staff.discipline === 'string'
          ? staff.discipline
          : staff.discipline?.status || staff.current_discipline_status || staff.discipline_status || 'SATISFACTORY';
      setStatus(initialStatus);
      setRemarks(staff.discipline?.remarks || '');
    }
  }, [staff, open]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!status) return;
    onSubmit({
      staffId: staff?.id,
      status,
      remarks,
    });
  };

  const availableEnums =
    statusEnums.length > 0
      ? statusEnums
      : [
          { label: 'Satisfactory', value: 'SATISFACTORY' },
          { label: 'Warning', value: 'WARNING' },
          { label: 'Major Warning', value: 'MAJOR_WARNING' },
          { label: 'Critical', value: 'CRITICAL' },
        ];

  const staffName = staff
    ? `${staff.first_name || ''} ${staff.last_name || ''}`.trim() || staff.name || 'Staff Member'
    : '';

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)',
        },
      }}
    >
      <DialogTitle sx={{ m: 0, px: 2.5, py: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: 2,
              bgcolor: 'primary.light',
              color: 'primary.main',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <ShieldAlert size={20} />
          </Box>
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
              Update Discipline Record
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
              Set status & official conduct notes
            </Typography>
          </Box>
        </Box>
        <IconButton onClick={onClose} disabled={loading} size="small">
          <X size={18} />
        </IconButton>
      </DialogTitle>

      <form onSubmit={handleSubmit}>
        <DialogContent dividers sx={{ px: 2.5, py: 2 }}>
          <Stack spacing= {2.5}>
            {/* Staff Info Banner */}
            {staff && (
              <Paper
                elevation={0}
                sx={{
                  p: 1.5,
                  bgcolor: 'action.hover',
                  borderRadius: 2,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  border: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <Avatar
                  src={staff.photo_url || staff.avatar_url || staff.profile_picture}
                  sx={{ width: 40, height: 40, bgcolor: 'primary.main', fontWeight: 600, fontSize: 16 }}
                >
                  {staffName.charAt(0)}
                </Avatar>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                    {staffName}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', lineHeight: 1.3, mt: 0.2 }}>
                    Code: <strong>{staff.employee_code || staff.staff_code || 'N/A'}</strong>
                    {staff.department?.name && ` • ${staff.department.name}`}
                  </Typography>
                </Box>
              </Paper>
            )}

            {/* Status Selection (Grid 2x2) */}
            <Box>
              <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5, mb: 1, display: 'block' }}>
                Select Status <Typography component="span" color="error.main">*</Typography>
              </Typography>
              
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(2, 1fr)',
                  gap: 1.2,
                }}
              >
                {availableEnums.map((item) => {
                  const isSelected = status === item.value;
                  const cfg = STATUS_CONFIG[item.value] || STATUS_CONFIG.SATISFACTORY;
                  const IconComp = cfg.icon;

                  return (
                    <Paper
                      key={item.value}
                      elevation={0}
                      onClick={() => setStatus(item.value)}
                      sx={{
                        p: 1.25,
                        cursor: 'pointer',
                        borderRadius: 2,
                        transition: 'all 0.15s ease-in-out',
                        border: '2px solid',
                        borderColor: isSelected ? cfg.borderColor : 'divider',
                        bgcolor: isSelected ? cfg.bgColor : 'background.paper',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.2,
                        boxShadow: isSelected ? `0 2px 8px ${cfg.borderColor}33` : 'none',
                        '&:hover': {
                          borderColor: cfg.borderColor,
                          bgcolor: isSelected ? cfg.bgColor : 'action.hover',
                        },
                      }}
                    >
                      <Box
                        sx={{
                          width: 28,
                          height: 28,
                          borderRadius: 1.5,
                          bgcolor: isSelected ? cfg.borderColor : 'action.hover',
                          color: isSelected ? '#FFFFFF' : cfg.textColor,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                          transition: 'all 0.15s ease',
                        }}
                      >
                        <IconComp size={16} />
                      </Box>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: isSelected ? 700 : 500,
                          color: isSelected ? cfg.textColor : 'text.primary',
                          fontSize: '0.825rem',
                          lineHeight: 1.2,
                        }}
                      >
                        {item.label || cfg.label}
                      </Typography>
                    </Paper>
                  );
                })}
              </Box>
            </Box>

            {/* Remarks Input */}
            <Box>
              <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5, mb: 1, display: 'block' }}>
                Remarks / Details
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={3}
                placeholder="Write official conduct notes or reasoning for status update..."
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
                disabled={loading}
                size="small"
                sx={{
                  '& .MuiInputBase-root': {
                    fontSize: '0.875rem',
                  },
                }}
              />
            </Box>
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 2.5, py: 1.5, gap: 1 }}>
          <Button onClick={onClose} disabled={loading} color="inherit" size="small" sx={{ textTransform: 'none', fontWeight: 600 }}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading || !status}
            startIcon={loading ? <CircularProgress size={16} color="inherit" /> : null}
            sx={{ fontWeight: 600, minWidth: 120, textTransform: 'none', borderRadius: 1.5 }}
          >
            {loading ? 'Saving...' : 'Save Record'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
